import CryptoJS from 'crypto-js';

import { config } from '../../config';

import { AuthError } from '../../api';
import { Sentry } from '../sentry';

const TOKEN_REQUEST_RETRY_ATTEMPTS = 7;
const TOKEN_REQUEST_RETRY_BASE_DELAY_MS = 250;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pickDebugHeaders = (headers) => {
  if (!headers || typeof headers.forEach !== 'function') return undefined;
  const allowlist = new Set([
    'x-request-id',
    'x-correlation-id',
    'request-id',
    'x-cache',
  ]);
  const picked = {};
  headers.forEach((value, key) => {
    if (allowlist.has(key.toLowerCase())) {
      picked[key] = value;
    }
  });
  return Object.keys(picked).length ? picked : undefined;
};

const parseJsonSafely = (text) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return { raw: text };
  }
};

const hashValue = (value) =>
  value ? CryptoJS.SHA256(String(value)).toString() : undefined;

/**
 * `options` is either the fetch options object or a function of the attempt
 * number, evaluated inside the loop. The function form is for requests whose
 * headers cannot be reused across attempts — a DPoP proof carries a
 * single-use `jti`, so every attempt has to sign a fresh one.
 *
 * `onResponse`, when given, sees every HTTP response before its body is read,
 * whatever its status — how a DPoP client picks up the `DPoP-Nonce` header a
 * server puts on a challenge as well as on a success.
 */
export const fetchJsonWithRetry = async (
  url,
  options,
  attempts = TOKEN_REQUEST_RETRY_ATTEMPTS,
  { onResponse } = {}
) => {
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const requestOptions =
        typeof options === 'function' ? await options(attempt) : options;
      const response = await fetch(url, requestOptions);
      if (onResponse) {
        onResponse(response);
      }
      const responseText = await response.text();
      const json = parseJsonSafely(responseText);

      if (!response.ok) {
        const error = new Error(
          `Token request failed with status ${response.status}`
        );
        error.name = 'TokenRequestError';
        error.status = response.status;
        error.response = json;
        error.headers = pickDebugHeaders(response.headers);
        error.url = url;
        throw error;
      }

      if (!json) {
        const error = new Error('Token response was empty or invalid');
        error.name = 'TokenResponseParseError';
        error.status = response.status;
        error.response = json;
        error.headers = pickDebugHeaders(response.headers);
        error.url = url;
        throw error;
      }

      return json;
    } catch (error) {
      lastError = error;
      const shouldRetry =
        attempt < attempts &&
        error?.name === 'TypeError' &&
        /Network request failed/i.test(error.message || '');
      Sentry.addBreadcrumb({
        message: 'Token request failed',
        category: 'auth',
        level: shouldRetry ? 'warning' : 'error',
        data: {
          url,
          attempt,
          will_retry: shouldRetry,
          http_status: error?.status,
          error_name: error?.name,
          error_message: error?.message,
        },
      });
      if (!shouldRetry) {
        throw error;
      }
      const delayMs = TOKEN_REQUEST_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      await sleep(delayMs);
    }
  }

  throw lastError;
};

export const getToken = async (data) => {
  const tokenUrl = `${config.api.host}/auth/token`;
  const username = config.api.client.user;
  const password = config.api.client.pass;

  // Need to it this way to have a cross platform way to do base64
  const wordArray = CryptoJS.enc.Utf8.parse(`${username}:${password}`);
  const base64AuthHeader = CryptoJS.enc.Base64.stringify(wordArray);

  // Request-only fields, shared by both failure captures below. Keys avoid
  // "token": Sentry blanks those server-side.
  const requestSummary = {
    request_url: tokenUrl,
    request_grant_type: data?.grant_type,
    request_redirect_uri: data?.redirect_uri,
    request_client_id: data?.client_id,
    request_has_code: Boolean(data?.code),
    request_has_code_verifier: Boolean(data?.code_verifier),
    request_code_hash: hashValue(data?.code),
    request_code_verifier_hash: hashValue(data?.code_verifier),
    request_has_refresh: Boolean(data?.refresh_token),
    request_has_subject: Boolean(data?.subject_token),
  };
  const grantType = data?.grant_type ?? 'unknown';
  // code_fp is the first 8 hex of sha256(code), the value formue-api tags on
  // its side, so one Sentry search finds both halves of a failed exchange.
  const tags = {
    internal_system: 'authentication',
    token_exchange: data?.grant_type,
    ...(data?.code ? { code_fp: hashValue(data.code).slice(0, 8) } : {}),
  };
  const startedAt = Date.now();

  let result;
  try {
    result = await fetchJsonWithRetry(tokenUrl, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${base64AuthHeader}`,
      },
    });
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      fingerprint: [
        'token-request',
        grantType,
        String(error?.status ?? 'network'),
      ],
      extra: {
        ...requestSummary,
        response_status: error?.status,
        response_body: error?.response,
        response_headers: error?.headers,
        response_url: error?.url,
        duration_ms: Date.now() - startedAt,
      },
      tags,
    });
    throw error;
  }

  if (result?.error) {
    Sentry.captureException(new Error('Error during token request'), {
      level: 'error',
      fingerprint: ['token-request', grantType, 'error-body'],
      extra: {
        ...requestSummary,
        response: result,
        duration_ms: Date.now() - startedAt,
      },
      tags,
    });
    throw new AuthError(result?.error);
  }

  return {
    accessToken: result.access_token,
    refreshToken: result.refresh_token,
  };
};

export const getDeepLink = (path = '') => {
  const prefix = `${config.app.urlScheme}://`;
  return prefix + path;
};

// SECURITY / TODO: this uses Math.random(), which is not cryptographically
// secure. It produces both the OAuth `state` (CSRF protection) and — via
// generateCodeVerifier — the PKCE `code_verifier`, which RFC 7636 §7.1 requires
// to be cryptographically random. CryptoJS is already a dependency here, so
// `CryptoJS.lib.WordArray.random` is a drop-in replacement. Left as-is for now.
export const generateRandomAuthStateString = (length = 32) => {
  let text = '';
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i += 1) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};

export function generateCodeVerifier() {
  const codeVerifier = generateRandomAuthStateString(50);
  return codeVerifier;
}

export function base64URL(string) {
  return string
    .toString(CryptoJS.enc.Base64)
    .replace(/[=]/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}

export function generateCodeChallenge(codeVerifier) {
  const codeChallenge = base64URL(CryptoJS.SHA256(codeVerifier));
  return codeChallenge;
}

/**
 * SHA-256 of a string (hashed as UTF-8, as CryptoJS does) as bytes — for
 * platforms without WebCrypto: the mobile DPoP signer's `ath` and thumbprint
 * digests (`dpopProof.js` takes the digest function as a parameter).
 */
export const sha256Bytes = (input) => {
  const { words, sigBytes } = CryptoJS.SHA256(input);
  const bytes = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i += 1) {
    bytes[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return bytes;
};
